import React, { useContext, useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { DisplaySpinnerContext } from "../../../../context/ContextIndex";
import {
    DataGridTable,
    GridAction,
    ConfirmationPopup,
    notifyToast,
    ToastType,
    FormPage,
} from "../../../../components-old/ComponentsIndex";
import { PricingPortalAPI } from "../../../../apis/api-index";
import { MdDelete, MdCreate } from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { saveDevice, getReplacementDevice } from "../../api-handler/api-handler";
import { useSelector } from "react-redux";
import { selectSessionConfig } from "../../../../store/store-selector";
import { AppUtility } from "../../../../utils/utility";
import { useFetchListData } from "../../../../hooks/useFetchListData";
import { getReplacementDeviceDefaultValue } from "../../replacement-device/create/schema";

const DeviceList = () => {
    /**************************************************
    //Form handlers & hooks
    **************************************************/
    const { t } = useTranslation();
    const navigate = useNavigate();
    const spinnerCtx = useContext(DisplaySpinnerContext);
    const settingsConfig = useSelector(selectSessionConfig);

    /**************************************************
    //Form state variables
    **************************************************/
    const [deviceRecordList, setDeviceRecordList] = useState([]);
    const [dataGridPageSize, setDataGridPageSize] = useState(10);
    const [totalDevices, setTotalDevices] = useState(0);
    const [gridPageSizeOptions /*setGridPageSizeOptions*/] = useState([10, 20, 50]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const DeviceTypeList = useFetchListData(settingsConfig.businessCode, settingsConfig.countryCode, "getDeviceType", "devicetype");
    const ServiceTypeList = useFetchListData(settingsConfig.businessCode, settingsConfig.countryCode, "getServiceType", "servicetype");


    /**************************************************
    //Event definitions
    **************************************************/

    //create new device
    const handleCreateDeviceClick = async () => {
        navigate("/device");
    };

    //on click of edit device in list
    const handleEditDeviceClick = async (param) => {
        let _navigationData = param.recordResponse;
        delete _navigationData.CreatedDate;
        delete _navigationData.ModifiedDate;
        delete _navigationData.CountryCode;
        delete _navigationData.BusinessCode;
        navigate(
            {
                pathname: "/device",
                search: createSearchParams({
                    deviceid: param.recordResponse.DeviceId,
                }).toString(),
            },
            { state: _navigationData }
        );
    };

    //on confirm of delete popup
    const handleConfirm = async () => {
        let _filterClause = `&deviceid=${selectedRow.deviceId}`;
        await fetchAvailablePezode(_filterClause, selectedRow);
        setPopupOpen(false);
        setSelectedRow(null);
    };

    //on click of cancel of delete popup
    const handleCancel = () => {
        setSelectedRow(null);
        setPopupOpen(false);
    };

    //on click of delete in list
    const handleDeleteDeviceClick = (event) => {
        try {
            setSelectedRow(event);
            setPopupOpen(true);
        } catch (err) {
            alert(err);
        }
    };

    //page size change
    const handlePageSizeChange = (newPageSize) => {
        setDataGridPageSize(newPageSize);
    };

    //show review modal
    const handleShowSelectedDeviceChanges = (event) => {
        let _navigationData = { previousPath: "/devicelist" };
        navigate(
            {
                pathname: "/reviewdetails",
                search: createSearchParams({
                    reviewFilter: `&locationkey=${event.id}`,
                }).toString(),
            },
            { state: _navigationData }
        );
    };

    /**************************************************
    //Fetch API & utility
    **************************************************/

    const setDeviceList = (deviceList) => {
        try {
            deviceList.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
            let _deviceList = deviceList.map((item) => {
                return {
                    id: item.Id,
                    deviceId: item.DeviceId,
                    shortName: item.shortName,
                    longName: item.LongName,
                    installationTime: item.InstallationTime,
                    maintenanceTime: item.MaintenanceTime,
                    agreementCost: item.AgreementCost,
                    grossMargin: item.GMMaterial,
                    recordResponse: item,
                };
            });

            setDeviceRecordList(_deviceList);
            setTotalDevices(_deviceList.length);
            spinnerCtx.setLoadingIndicator(false);
        } catch (err) {
            spinnerCtx.setLoadingIndicator(false);
            alert(err);
        }
    };

    const FetchDeviceList = async () => {
        try {
            let _lists = await PricingPortalAPI.getList(
                settingsConfig.businessCode,
                settingsConfig.countryCode,
                "getDeviceList",
                "device"
            );
            await setDeviceList(_lists);
        } catch (err) {
            alert(err);
        }
    };

    //Fetch API to get pezode with the same device before deleting device
    const fetchAvailablePezode = async (filter, param) => {
        console.log("Inside fetchAvailablePezode param ::::", param);
        try {
            let _availablePezode = await PricingPortalAPI.getFilterList(
                settingsConfig.businessCode,
                settingsConfig.countryCode,
                "getPezodeList",
                "pezodein",
                filter
            );

            if (_availablePezode && _availablePezode.length > 0) {
                notifyToast("Pezode combination exists with this device", ToastType.ERROR);
            } else {
                await deleteDeviceRecord(param);
            }
        } catch (e) {
            alert(e);
        }
    };

    const formPreviousDataForDelete = async (param, editDeviceId) => {
        try {
            let _previousState = {}
            _previousState.device = param.recordResponse;
            _previousState.Deleted = true;
            delete _previousState.device.CreatedDate;
            delete _previousState.device.ModifiedDate;
            delete _previousState.device.CountryCode;
            delete _previousState.device.BusinessCode;
            delete _previousState.device.Version;
            delete _previousState.device.DeviceId;
            delete _previousState.device.Status;

            DeviceTypeList.forEach((item) => {
                if (item.DeviceTypeId === _previousState.device.DeviceTypeId) {
                    _previousState.device.DeviceType = {
                        id: item.DeviceTypeId,
                        label: item.TypeName,
                    };
                }
            });

            ServiceTypeList.forEach((item) => {
                if (item.ServiceTypeId === _previousState.device.TypeId) {
                    _previousState.device.Type = {
                        id: item.ServiceTypeId,
                        label: item.ServiceName,
                    };
                }
            });


            let response = await getReplacementDevice(editDeviceId, settingsConfig);
            _previousState.replacementDevice = response.map((item, index) => ({
                id: index,
                ...getReplacementDeviceDefaultValue(item)
            }));
            return _previousState ? _previousState : null;
        } catch (e) {
            alert(e);
        }
    };

    //API to delete device
    const deleteDeviceRecord = async (param) => {
        try {
            spinnerCtx.setLoadingIndicator(true);

            let editDeviceId = param.id;
            let data = await formPreviousDataForDelete(param, editDeviceId);

            const status = await saveDevice(data, {
                editDeviceId,
                settingsConfig,
                formdata: data
            });
            if (status?.responseStatus === 200) {
                notifyToast("Sent for Approval", ToastType.SUCCESS);
                await FetchDeviceList();
            } else {
                notifyToast(status.errorMessage ? status.errorMessage : "Device deletion failed", ToastType.ERROR);
                spinnerCtx.setLoadingIndicator(false);
            }
        } catch (err) {
            alert(err);
        }
    };

    /**************************************************
    //JSX delcaration
    **************************************************/
    const dataGridSchema = [
        {
            field: "shortName",
            headerName: t("deviceList.shortName"),
            flex: 2,
        },
        {
            field: "longName",
            headerName: t("deviceList.longName"),
            flex: 2,
        },
        {
            field: "installationTime",
            headerName: t("deviceList.installationTime"),
            flex: 1,
        },
        {
            field: "maintenanceTime",
            headerName: t("deviceList.maintenanceTime"),
            flex: 1,
        },
        {
            field: "agreementCost",
            headerName: t("deviceList.agreementCost"),
            flex: 1,
        },
        {
            field: "grossMargin",
            headerName: t("deviceList.grossMargin"),
            flex: 1,
        },
        {
            field: "recordResponse",
            hide: true,
        },
        {
            field: "action",
            headerName: t,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                return (
                    <GridAction actionsConfig={actionConfigs} row={params.row} />
                );
            },
        },
    ];

    const buttonConfigs = [
        {
            buttonText: <AddIcon />,
            buttonType: "filled",
            onClick: handleCreateDeviceClick,
            buttonColor: "#A62B23",
            textColor: "#fff",
            action: "create"
        },
        {
            buttonText: <SystemUpdateAltIcon />,
            buttonType: "outlined",
            onClick: () => {
                AppUtility.handleDownload(deviceRecordList, "devicelist.xlsx");
            },
            buttonColor: "#A62B23",
            textColor: "#A62B23",
            action: "download"
        },
    ];

    //action configs for edit and delete record
    const actionConfigs = [
        {
            onClick: handleShowSelectedDeviceChanges,
            action: "review"
        },
        {
            icon: MdCreate,
            onClick: handleEditDeviceClick,
            buttonColor: "#EEEEE",
            action: "edit"
        },
        {
            icon: MdDelete,
            onClick: handleDeleteDeviceClick,
            buttonColor: "#A62B23",
            action: "delete"
        },
    ];

    /**************************************************
    //Form on load useEffect
    **************************************************/
    useEffect(() => {
        spinnerCtx.setLoadingIndicator(true);
        FetchDeviceList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <FormPage isList={true}>
            <DataGridTable
                rows={deviceRecordList}
                columns={dataGridSchema}
                pageSize={dataGridPageSize}
                pageSizeOptions={gridPageSizeOptions}
                onPageSizeChange={handlePageSizeChange}
                totalRowCount={totalDevices}
                toolbarHeader={t("deviceList.header")}
                toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
                buttonConfigs={buttonConfigs}
            />
            <ConfirmationPopup
                isOpen={popupOpen}
                onClose={handleCancel}
                onConfirm={handleConfirm}
                confirmationText={t("popup.deleteDeviceConfirmationMessage")}
                eventObj={selectedRow}
            />
        </FormPage>
    );
};

export default DeviceList;
